/**
 * DonateAmountsPage - Streamlined donation flow (mobile-first)
 *
 * Route: /donate-amounts
 *
 * 3-step flow:
 *   Step 1 – Select amount + donation type (side-by-side on tablet, stacked on mobile)
 *   Step 2 – Cart summary + donor info (email & name) with "add more" option
 *   Step 3 – Confirm & pay (Stripe redirect)
 *
 * Query-param shortcut:
 *   ?amount=100&donationType=5  → auto-adds to cart, jumps to step 2
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useCart } from '@/contexts/CartContext';
import { useCompany } from '@/contexts/CompanyContext';
import { useAuth } from '@/contexts/AuthContext';
import { useStripeCheckout } from '@/hooks/useStripeCheckout';
import { getDonationTypes } from '@/services/api/donationTypeService';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ErrorMessage } from '@/components/common/ErrorMessage';
import { WelcomeMessageDonate } from '@/components/features/donation';
import { env } from '@/config/env';
import type { DonationType } from '@/types/donation.types';
import type { TransactionModel, TransactionDetail } from '@/types';
import {
  ShoppingCartIcon,
  TrashIcon,
  PlusCircleIcon,
  CheckCircleIcon,
  HeartIcon,
  CurrencyDollarIcon,
  UserIcon,
  CreditCardIcon,
  ArrowRightIcon,
  ArrowLeftIcon,
  ChevronDownIcon,
  CalendarDaysIcon,
} from '@heroicons/react/24/outline';
import { HeartIcon as HeartSolidIcon } from '@heroicons/react/24/solid';
import clsx from 'clsx';
import {
  LARGE_AMOUNT_THRESHOLD,
  RECURRING_FREQUENCIES,
  RECURRING_COUNTS,
  DEFAULT_RECURRING_FREQUENCY,
  DEFAULT_RECURRING_COUNT,
  getFrequencyInfo,
} from '@/constants/recurringOptions';

// ---------------------------------------------------------------------------
// Preset amounts (fits in 3×3 grid on mobile)
// ---------------------------------------------------------------------------
const PRESET_AMOUNTS = [50, 100, 250, 500, 1000, 1400, 2000, 2800, 5000];

/** Number of causes visible before "Load More" is clicked */
const CAUSE_INITIAL_LIMIT = 3;

type Step = 'select' | 'info' | 'pay';

export function DonateAmountsPage() {
  const [searchParams] = useSearchParams();
  const { companyIdNumber } = useCompany();
  const { isAuthenticated, user, donor } = useAuth();
  const { items, addItem, removeItem, clearCartAndSetPurpose, getTotalAmount } = useCart();
  const { loading: stripeLoading, error: stripeError, createSession } = useStripeCheckout();

  // ---- local state ----
  const [step, setStep] = useState<Step>('select');
  const [selectedAmount, setSelectedAmount] = useState<number | null>(null);
  const [customAmount, setCustomAmount] = useState('');
  const [selectedDonationType, setSelectedDonationType] = useState<DonationType | null>(null);
  const [donationTypes, setDonationTypes] = useState<DonationType[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Checkout fields
  const [donorEmail, setDonorEmail] = useState('');
  const [donorName, setDonorName] = useState('');
  const [emailError, setEmailError] = useState('');
  const [nameError, setNameError] = useState('');
  const [addProcessingFees, setAddProcessingFees] = useState(true);
  const [isDonateAnonymously, setIsDonateAnonymously] = useState(false);
  const [isRecurring, setIsRecurring] = useState(true);
  const [recurringFrequency, setRecurringFrequency] = useState(DEFAULT_RECURRING_FREQUENCY);
  const [recurringCount, setRecurringCount] = useState(DEFAULT_RECURRING_COUNT);
  // True only when the user explicitly chose "spread it out" in Step 1
  const [isSpreadPayments, setIsSpreadPayments] = useState(false);

  // Track whether query-param auto-add has been processed
  const [autoAddDone, setAutoAddDone] = useState(false);

  // Cause list visibility
  const [showAllCauses, setShowAllCauses] = useState(false);

  // ---- derived ----
  const totalAmount = getTotalAmount(); // cents — full cart total
  // Divide only when the user explicitly chose to spread payments in Step 1
  const chargeSubtotalCents =
    isSpreadPayments && recurringCount > 1 ? Math.round(totalAmount / recurringCount) : totalAmount;
  const processingFees = useMemo(() => {
    if (!addProcessingFees) return 0;
    return Math.round(
      chargeSubtotalCents * env.onlineFeePercentage + env.onlineFeeFixedAmountCents
    );
  }, [chargeSubtotalCents, addProcessingFees]);
  const finalTotal = chargeSubtotalCents + processingFees;

  const activeAmount = selectedAmount ?? (customAmount ? parseFloat(customAmount) : null);
  const canAddToCart = activeAmount && activeAmount > 0 && selectedDonationType !== null;

  // Step indicator data
  const steps: { key: Step; label: string; icon: typeof HeartIcon }[] = [
    { key: 'select', label: 'Choose', icon: HeartIcon },
    { key: 'info', label: 'Details', icon: UserIcon },
    { key: 'pay', label: 'Pay', icon: CreditCardIcon },
  ];
  const currentStepIndex = steps.findIndex((s) => s.key === step);

  // ---- clear cart on mount and set purpose ----
  useEffect(() => {
    clearCartAndSetPurpose(1);
  }, [clearCartAndSetPurpose]);

  // ---- Pre-populate donor fields from auth ----
  useEffect(() => {
    if (isAuthenticated) {
      const email = donor?.eMail || user?.email || '';
      const name =
        donor?.completeName ||
        (donor?.firstName && donor?.lastName
          ? `${donor.firstName} ${donor.lastName}`.trim()
          : undefined) ||
        user?.name ||
        '';
      setDonorEmail(email);
      setDonorName(name);
    }
  }, [isAuthenticated, donor, user]);

  // ---- Load donation types ----
  useEffect(() => {
    const loadData = async () => {
      if (!companyIdNumber) return;
      try {
        setIsLoading(true);
        setError(null);
        const data = await getDonationTypes(companyIdNumber);
        const webDonations = data.items.filter((dt) => dt.showWeb === 1);
        setDonationTypes(webDonations);
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to load donation data'));
      } finally {
        setIsLoading(false);
      }
    };
    loadData();
  }, [companyIdNumber]);

  // ---- Query-param shortcut ----
  useEffect(() => {
    if (autoAddDone || isLoading || donationTypes.length === 0) return;

    const qtyAmount = searchParams.get('amount');
    const qtyType = searchParams.get('donationType');

    if (qtyAmount && qtyType) {
      const amt = parseFloat(qtyAmount);
      const typeId = parseInt(qtyType, 10);
      const dt = donationTypes.find((d) => d.donationType === typeId);

      if (amt > 0 && dt) {
        addItem({
          donationType: dt,
          amount: Math.round(amt * 100),
          purposeId: 1,
        });
        setStep('info');
      }
    }
    setAutoAddDone(true);
  }, [autoAddDone, isLoading, donationTypes, searchParams, addItem]);

  // ---- helpers ----
  const handleAddToCartAndContinue = useCallback(() => {
    if (!activeAmount || activeAmount <= 0 || !selectedDonationType) return;
    // If the user did NOT spread payments, reset spreading state
    if (!isSpreadPayments) {
      setIsRecurring(true);
      setRecurringFrequency(DEFAULT_RECURRING_FREQUENCY);
      setRecurringCount(DEFAULT_RECURRING_COUNT);
    }
    addItem({
      donationType: selectedDonationType,
      amount: Math.round(activeAmount * 100),
      purposeId: 1,
    });
    setSelectedAmount(null);
    setCustomAmount('');
    setSelectedDonationType(null);
    setStep('info');
  }, [activeAmount, selectedDonationType, isSpreadPayments, addItem]);

  const handleAddMore = useCallback(() => {
    setSelectedAmount(null);
    setCustomAmount('');
    setSelectedDonationType(null);
    setShowAllCauses(false);
    setIsSpreadPayments(false);
    setStep('select');
  }, []);

  const handleProceedToPay = useCallback(() => {
    // validate
    if (!donorEmail.trim()) {
      setEmailError('Email is required');
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(donorEmail)) {
      setEmailError('Please enter a valid email address');
      return;
    }
    setEmailError('');

    if (!env.onlineEnableAnonymous || !isDonateAnonymously) {
      if (!donorName.trim()) {
        setNameError('Full name is required');
        return;
      }
      setNameError('');
    }

    setStep('pay');
  }, [donorEmail, donorName, isDonateAnonymously]);

  const handleConfirmPayment = useCallback(async () => {
    if (!companyIdNumber || items.length === 0) return;

    const details: TransactionDetail[] = items.map((item, i) => ({
      lineNumber: i + 1,
      description:
        item.donationType?.detailedTextHeader || item.donationType?.description || 'Donation',
      donationTypeId: item.donationType?.donationType || 0,
      amount:
        isSpreadPayments && recurringCount > 1
          ? Math.round(item.amount / recurringCount) / 100
          : item.amount / 100,
    }));

    if (addProcessingFees && processingFees > 0) {
      details.push({
        lineNumber: details.length + 1,
        description: env.onlineFeeDonationTypeDescription,
        donationTypeId: env.onlineFeeDonationTypeId,
        amount: processingFees / 100,
      });
    }

    const request: TransactionModel = {
      transactionId: 0,
      companyId: companyIdNumber,
      donorId: env.onlineEnableAnonymous && isDonateAnonymously ? env.anonymousDonorId : 0,
      donorName: env.onlineEnableAnonymous && isDonateAnonymously ? 'Anonymous' : donorName.trim(),
      donorEmail: donorEmail.trim(),
      transactionDate: new Date().toISOString(),
      purposeId: 1,
      totalAmount: finalTotal / 100,
      currency: env.onlineCurrency.toUpperCase(),
      recurring: isRecurring,
      numberofMonths: isRecurring ? recurringCount : undefined,
      recurringFrequency: isRecurring ? recurringFrequency : undefined,
      successUrl: `${window.location.origin}/success?session_id={CHECKOUT_SESSION_ID}`,
      cancelUrl: `${window.location.origin}/cancel`,
      details,
    };

    try {
      await createSession(request);
    } catch {
      // stripeError state is set by the hook
    }
  }, [
    companyIdNumber,
    items,
    addProcessingFees,
    processingFees,
    finalTotal,
    donorName,
    donorEmail,
    isDonateAnonymously,
    isRecurring,
    isSpreadPayments,
    recurringFrequency,
    recurringCount,
    createSession,
  ]);

  // ---- loading / error ----
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <LoadingSpinner size="lg" message="Loading..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[60vh] p-4">
        <div className="w-full max-w-md">
          <ErrorMessage error={error} />
        </div>
      </div>
    );
  }

  // ======================================================================
  //  STEP renderers
  // ======================================================================

  // -- Shared step progress bar --
  const StepIndicator = () => (
    <div className="flex items-center justify-center gap-1 mb-2">
      {steps.map((s, i) => {
        const Icon = s.icon;
        const isActive = i === currentStepIndex;
        const isDone = i < currentStepIndex;
        return (
          <div key={s.key} className="flex items-center">
            <div
              className={clsx(
                'flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all',
                isActive
                  ? 'bg-primary-600 text-white shadow-md'
                  : isDone
                    ? 'bg-primary-100 text-primary-700'
                    : 'bg-gray-100 text-gray-400'
              )}
            >
              <Icon className="h-3.5 w-3.5" />
              <span className="hidden xs:inline">{s.label}</span>
            </div>
            {i < steps.length - 1 && (
              <div
                className={clsx(
                  'w-6 h-0.5 mx-0.5 rounded-full',
                  i < currentStepIndex ? 'bg-primary-400' : 'bg-gray-200'
                )}
              />
            )}
          </div>
        );
      })}
    </div>
  );

  // -- Step 1: Select Amount + Donation Type (combined) --
  if (step === 'select') {
    return (
      <div className="w-full mx-auto px-1 py-1 space-y-1">
        <WelcomeMessageDonate />
        <StepIndicator />

        {/* Hero banner */}
        <div className="relative overflow-hidden rounded-2xl bg-linear-to-br from-primary-600 via-primary-500 to-primary-400 p-5 text-center text-white shadow-lg">
          <div className="absolute -top-4 -right-4 opacity-10">
            <HeartSolidIcon className="h-28 w-28" />
          </div>
          <HeartSolidIcon className="h-8 w-8 mx-auto mb-1 drop-shadow" />
          <h1 className="text-xl font-bold tracking-tight">Make a Difference Today</h1>
          <p className="text-primary-100 text-sm mt-1">Choose an amount and a cause to support</p>
        </div>

        {/* Cards: Amount + Plan (left col) | Cause (right col) on lg+ */}
        <div className="lg:grid lg:grid-cols-2 lg:gap-5 lg:items-start space-y-3 lg:space-y-0">
          {/* ── Left column ── */}
          <div className="space-y-3">
            {/* Amount selection */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 space-y-3">
              <h2 className="text-base font-bold text-gray-800 flex items-center gap-2">
                <CurrencyDollarIcon className="h-5 w-5 text-primary-500" />
                Select Amount
              </h2>

              <div className="grid grid-cols-3 gap-2">
                {PRESET_AMOUNTS.map((amt) => (
                  <button
                    key={amt}
                    onClick={() => {
                      setSelectedAmount(amt);
                      setCustomAmount('');
                      if (amt >= LARGE_AMOUNT_THRESHOLD) {
                        setIsRecurring(true);
                        setRecurringFrequency(DEFAULT_RECURRING_FREQUENCY);
                        setRecurringCount(DEFAULT_RECURRING_COUNT);
                      }
                    }}
                    className={clsx(
                      'py-3 rounded-xl text-base font-semibold transition-all duration-200 border-2',
                      selectedAmount === amt
                        ? 'bg-primary-600 text-white border-primary-600 shadow-lg shadow-primary-200 scale-[1.03]'
                        : 'bg-white text-gray-800 border-gray-200 hover:border-primary-400 hover:bg-primary-50 hover:shadow-sm'
                    )}
                  >
                    ${amt}
                  </button>
                ))}
              </div>

              {/* Custom amount */}
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  Or enter a custom amount
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg font-medium">
                    $
                  </span>
                  <input
                    type="number"
                    value={customAmount}
                    onChange={(e) => {
                      setCustomAmount(e.target.value);
                      setSelectedAmount(null);
                      const val = parseFloat(e.target.value);
                      if (!isNaN(val) && val >= LARGE_AMOUNT_THRESHOLD) {
                        setIsRecurring(true);
                        setRecurringFrequency(DEFAULT_RECURRING_FREQUENCY);
                        setRecurringCount(DEFAULT_RECURRING_COUNT);
                      }
                    }}
                    placeholder="0.00"
                    min="1"
                    step="0.01"
                    className="w-full pl-8 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-lg transition-colors"
                  />
                </div>
              </div>
            </div>

            {/* Payment plan prompt — only for amounts ≥ $1,000 */}
            {activeAmount !== null && activeAmount >= LARGE_AMOUNT_THRESHOLD && (
              <div
                className={clsx(
                  'rounded-2xl border-2 p-5 space-y-4 transition-all duration-200',
                  isRecurring
                    ? 'bg-primary-50 border-primary-300 shadow-md shadow-primary-100'
                    : 'bg-gray-50 border-gray-200 shadow-sm'
                )}
              >
                {/* Header */}
                <div className="flex items-center gap-3">
                  <div className="shrink-0 w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                    <CurrencyDollarIcon className="h-5 w-5 text-primary-600" />
                  </div>
                  <div>
                    <p className="font-bold text-gray-900 text-base">
                      Do you want to pay this over time?
                    </p>
                    <p className="text-sm text-gray-500 mt-0.5">
                      {isRecurring
                        ? `${recurringCount} payment${recurringCount > 1 ? 's' : ''} of $${(activeAmount / recurringCount).toFixed(2)} / ${getFrequencyInfo(recurringFrequency).unit}`
                        : `One payment of $${activeAmount.toFixed(2)}`}
                    </p>
                  </div>
                </div>

                {/* Yes / No selector */}
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setIsSpreadPayments(true);
                      setIsRecurring(true);
                    }}
                    className={clsx(
                      'flex-1 py-3 rounded-xl border-2 text-sm font-bold transition-all',
                      isSpreadPayments
                        ? 'bg-primary-600 text-white border-primary-600 shadow-md shadow-primary-200'
                        : 'bg-white text-gray-700 border-gray-300 hover:border-primary-400 hover:bg-primary-50'
                    )}
                  >
                    Yes, spread it out
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setIsSpreadPayments(false);
                      setIsRecurring(false);
                    }}
                    className={clsx(
                      'flex-1 py-3 rounded-xl border-2 text-sm font-bold transition-all',
                      !isSpreadPayments
                        ? 'bg-gray-700 text-white border-gray-700 shadow-md'
                        : 'bg-white text-gray-700 border-gray-300 hover:border-gray-400 hover:bg-gray-50'
                    )}
                  >
                    No, pay at once
                  </button>
                </div>

                {/* Frequency + count when spreading */}
                {isSpreadPayments && (
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wide">
                        How often?
                      </label>
                      <div className="flex gap-2">
                        {RECURRING_FREQUENCIES.map(({ label, value, defaultCount }) => (
                          <button
                            key={value}
                            type="button"
                            onClick={() => {
                              setRecurringFrequency(value);
                              setRecurringCount(defaultCount);
                            }}
                            className={clsx(
                              'flex-1 py-2.5 text-sm font-bold rounded-xl border-2 transition-all',
                              recurringFrequency === value
                                ? 'bg-primary-600 text-white border-primary-600 shadow-md shadow-primary-200 scale-[1.03]'
                                : 'bg-white text-gray-700 border-gray-300 hover:border-primary-400 hover:bg-primary-50'
                            )}
                          >
                            {label}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label
                        htmlFor="plan-count"
                        className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wide"
                      >
                        {getFrequencyInfo(recurringFrequency).countQuestion}
                      </label>
                      <select
                        id="plan-count"
                        value={recurringCount}
                        onChange={(e) => setRecurringCount(Number(e.target.value))}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl text-gray-900 text-base font-medium focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white"
                      >
                        {(RECURRING_COUNTS[recurringFrequency] ?? []).map(({ value, label }) => (
                          <option key={value} value={value}>
                            {label}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Per-payment breakdown */}
                    <div className="bg-primary-100 rounded-xl p-3 text-center">
                      <p className="text-base font-bold text-primary-800">
                        ${(activeAmount / recurringCount).toFixed(2)} /{' '}
                        {getFrequencyInfo(recurringFrequency).unit}
                      </p>
                      <p className="text-xs text-primary-600 mt-0.5">
                        {recurringCount} payment{recurringCount > 1 ? 's' : ''} of $
                        {(activeAmount / recurringCount).toFixed(2)}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          {/* /left column */}

          {/* ── Right column ── */}
          <div>
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 space-y-3">
              <h2 className="text-base font-bold text-gray-800 flex items-center gap-2">
                <HeartIcon className="h-5 w-5 text-primary-500" />
                Select Cause
              </h2>

              {donationTypes.length === 0 ? (
                <p className="text-center text-gray-500 py-4">No donation types available.</p>
              ) : (
                <>
                  <div className="space-y-2">
                    {donationTypes
                      .slice(0, showAllCauses ? donationTypes.length : CAUSE_INITIAL_LIMIT)
                      .map((dt) => (
                        <button
                          key={dt.donationType}
                          onClick={() => setSelectedDonationType(dt)}
                          className={clsx(
                            'w-full text-left p-3 border-2 rounded-xl transition-all duration-200',
                            selectedDonationType?.donationType === dt.donationType
                              ? 'border-primary-500 bg-primary-50 shadow-md shadow-primary-100 ring-1 ring-primary-200'
                              : 'border-gray-200 bg-white hover:border-primary-300 hover:bg-gray-50 hover:shadow-sm'
                          )}
                        >
                          <span className="font-semibold text-gray-900 text-sm">
                            {dt.description || dt.shortName || `Type ${dt.donationType}`}
                          </span>
                          {dt.detailedText && (
                            <span className="block text-xs text-gray-500 mt-0.5">
                              {dt.detailedText}
                            </span>
                          )}
                        </button>
                      ))}
                  </div>

                  {!showAllCauses && donationTypes.length > CAUSE_INITIAL_LIMIT && (
                    <button
                      type="button"
                      onClick={() => setShowAllCauses(true)}
                      className="w-full flex items-center justify-center gap-1.5 py-2.5 border-2 border-dashed border-gray-300 text-gray-500 text-sm font-semibold rounded-xl hover:border-primary-400 hover:text-primary-600 hover:bg-primary-50 transition-all"
                    >
                      <ChevronDownIcon className="h-4 w-4" />
                      Load More ({donationTypes.length - CAUSE_INITIAL_LIMIT} more)
                    </button>
                  )}
                </>
              )}
            </div>
            {/* /cause card */}
          </div>
          {/* /right column */}
        </div>
        {/* /cards grid */}

        {/* Selection summary + Continue */}
        <div className="bg-linear-to-r from-primary-50 to-white rounded-2xl border border-primary-100 p-4 space-y-3">
          {activeAmount && activeAmount > 0 && selectedDonationType ? (
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">
                <span className="font-bold text-primary-700 text-lg">
                  ${activeAmount.toFixed(2)}
                </span>{' '}
                &rarr;{' '}
                <span className="font-medium text-gray-800">
                  {selectedDonationType.description || selectedDonationType.shortName}
                </span>
              </span>
              <CheckCircleIcon className="h-5 w-5 text-green-500" />
            </div>
          ) : (
            <p className="text-sm text-gray-400 text-center">
              {!activeAmount || activeAmount <= 0 ? 'Pick an amount above' : 'Now select a cause'}
            </p>
          )}

          <button
            onClick={handleAddToCartAndContinue}
            disabled={!canAddToCart}
            className="w-full py-4 bg-linear-to-r from-primary-600 to-primary-500 text-white font-semibold rounded-xl hover:from-primary-700 hover:to-primary-600 disabled:opacity-40 disabled:cursor-not-allowed transition-all text-lg shadow-md hover:shadow-lg disabled:shadow-none flex items-center justify-center gap-2"
          >
            Continue
            <ArrowRightIcon className="h-5 w-5" />
          </button>
        </div>
      </div>
    );
  }

  // -- Step 2: Cart + Donor Info --
  if (step === 'info') {
    return (
      <div className="w-full mx-auto px-3 py-3 space-y-4">
        <StepIndicator />

        {/* Cart summary card */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 space-y-3">
          <h2 className="text-base font-bold text-gray-800 flex items-center gap-2">
            <ShoppingCartIcon className="h-5 w-5 text-primary-500" />
            Your Donations
          </h2>

          {items.length === 0 ? (
            <div className="text-center py-6 space-y-3">
              <div className="mx-auto w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center">
                <ShoppingCartIcon className="h-7 w-7 text-gray-300" />
              </div>
              <p className="text-gray-500 text-sm">Your cart is empty</p>
              <button
                onClick={handleAddMore}
                className="py-2.5 px-5 bg-linear-to-r from-primary-600 to-primary-500 text-white text-sm font-semibold rounded-xl hover:from-primary-700 hover:to-primary-600 transition-all shadow-md"
              >
                Add a Donation
              </button>
            </div>
          ) : (
            <>
              <div className="divide-y divide-gray-100">
                {items.map((item) => (
                  <div key={item.id} className="flex items-center justify-between py-3 group">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-900 truncate">
                        {item.donationType?.detailedTextHeader ||
                          item.donationType?.description ||
                          'Donation'}
                      </div>
                      <div className="text-sm font-bold text-primary-600">
                        ${(item.amount / 100).toFixed(2)}
                      </div>
                    </div>
                    <button
                      onClick={() => removeItem(item.id)}
                      className="ml-2 p-2 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
                      aria-label="Remove"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>

              <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                <span className="text-sm font-semibold text-gray-700">Subtotal</span>
                <span className="text-xl font-bold text-primary-600">
                  ${(totalAmount / 100).toFixed(2)}
                </span>
              </div>

              <button
                onClick={handleAddMore}
                className="w-full flex items-center justify-center gap-2 py-2.5 border-2 border-dashed border-gray-300 text-gray-600 text-sm font-semibold rounded-xl hover:border-primary-400 hover:text-primary-600 hover:bg-primary-50 transition-all"
              >
                <PlusCircleIcon className="h-5 w-5" />
                Add Another Donation
              </button>
            </>
          )}
        </div>

        {/* Donor info form */}
        {items.length > 0 && (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 space-y-4">
            <h2 className="text-base font-bold text-gray-800 flex items-center gap-2">
              <UserIcon className="h-5 w-5 text-primary-500" />
              Your Information
            </h2>

            {isAuthenticated && user && (
              <div className="flex items-center gap-2.5 px-3 py-2.5 bg-green-50 border border-green-200 rounded-xl text-sm text-green-800">
                {user.picture && (
                  <img
                    src={user.picture}
                    alt={user.name || 'User'}
                    className="h-7 w-7 rounded-full object-cover ring-2 ring-green-200"
                    referrerPolicy="no-referrer"
                    crossOrigin="anonymous"
                  />
                )}
                <span>
                  Signed in as <strong>{user.name || user.email}</strong>
                </span>
              </div>
            )}

            {/* Anonymous toggle */}
            {env.onlineEnableAnonymous && (
              <label className="flex items-center gap-3 cursor-pointer group p-3 rounded-xl hover:bg-gray-50 transition-colors">
                <input
                  type="checkbox"
                  checked={isDonateAnonymously}
                  onChange={(e) => {
                    setIsDonateAnonymously(e.target.checked);
                    if (e.target.checked) {
                      setNameError('');
                    }
                  }}
                  className="h-5 w-5 rounded border-gray-300 text-primary-600 focus:ring-primary-500 cursor-pointer"
                />
                <span className="text-sm text-gray-700 group-hover:text-gray-900">
                  Donate Anonymously
                </span>
              </label>
            )}

            {/* Email */}
            <div>
              <label htmlFor="da-email" className="block text-sm font-medium text-gray-700 mb-1">
                Email Address <span className="text-red-500">*</span>
              </label>
              <input
                id="da-email"
                type="email"
                value={donorEmail}
                onChange={(e) => {
                  setDonorEmail(e.target.value);
                  setEmailError('');
                }}
                disabled={isDonateAnonymously}
                placeholder="your.email@example.com"
                className={clsx(
                  'w-full px-4 py-3 border-2 rounded-xl text-gray-900 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-50 disabled:cursor-not-allowed transition-colors',
                  emailError ? 'border-red-400 bg-red-50' : 'border-gray-200'
                )}
              />
              {emailError && <p className="mt-1 text-sm text-red-600">{emailError}</p>}
              <p className="mt-1 text-xs text-gray-400">Receipt will be sent to this email</p>
            </div>

            {/* Name (hidden when anonymous) */}
            {(!env.onlineEnableAnonymous || !isDonateAnonymously) && (
              <div>
                <label htmlFor="da-name" className="block text-sm font-medium text-gray-700 mb-1">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <input
                  id="da-name"
                  type="text"
                  value={donorName}
                  onChange={(e) => {
                    setDonorName(e.target.value);
                    setNameError('');
                  }}
                  placeholder="John Doe"
                  className={clsx(
                    'w-full px-4 py-3 border-2 rounded-xl text-gray-900 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors',
                    nameError ? 'border-red-400 bg-red-50' : 'border-gray-200'
                  )}
                />
                {nameError && <p className="mt-1 text-sm text-red-600">{nameError}</p>}
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <button
                onClick={handleAddMore}
                className="flex-1 py-3 bg-gray-100 text-gray-700 font-semibold rounded-xl hover:bg-gray-200 transition-colors flex items-center justify-center gap-1.5"
              >
                <ArrowLeftIcon className="h-4 w-4" />
                Back
              </button>
              <button
                onClick={handleProceedToPay}
                className="flex-1 py-3 bg-linear-to-r from-primary-600 to-primary-500 text-white font-semibold rounded-xl hover:from-primary-700 hover:to-primary-600 transition-all shadow-md hover:shadow-lg flex items-center justify-center gap-1.5"
              >
                Continue
                <ArrowRightIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // -- Step 3: Confirm & Pay --
  if (step === 'pay') {
    return (
      <div className="w-full mx-auto px-3 py-3 space-y-4">
        <StepIndicator />

        {/* Hero */}
        <div className="relative overflow-hidden rounded-2xl bg-linear-to-br from-primary-700 via-primary-600 to-primary-500 p-5 text-center text-white shadow-lg">
          <div className="absolute -bottom-3 -left-3 opacity-10">
            <CreditCardIcon className="h-24 w-24" />
          </div>
          <CreditCardIcon className="h-8 w-8 mx-auto mb-1 drop-shadow" />
          <h1 className="text-xl font-bold tracking-tight">Confirm & Pay</h1>
          <p className="text-primary-100 text-sm mt-1">Review your donation before payment</p>
        </div>

        {/* Summary */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 space-y-3">
          {items.map((item) => (
            <div key={item.id} className="flex justify-between text-sm">
              <span className="text-gray-700">
                {item.donationType?.detailedTextHeader ||
                  item.donationType?.description ||
                  'Donation'}
              </span>
              <span className="font-medium text-gray-900">${(item.amount / 100).toFixed(2)}</span>
            </div>
          ))}
          <hr className="border-gray-200" />

          {/* Total commitment — always shown */}
          <div className="flex justify-between text-sm">
            <span className="text-gray-700">
              {isSpreadPayments ? 'Total commitment' : 'Subtotal'}
            </span>
            <span className="font-medium">${(totalAmount / 100).toFixed(2)}</span>
          </div>

          {/* Payment plan summary — only when spreading across payments */}
          {isSpreadPayments && isRecurring && (
            <div className="flex items-center gap-2.5 bg-primary-50 border border-primary-200 rounded-xl px-3 py-2.5">
              <CalendarDaysIcon className="h-4 w-4 text-primary-500 shrink-0" />
              <p className="text-xs text-primary-700">
                <span className="font-bold">
                  {recurringCount} {getFrequencyInfo(recurringFrequency).label.toLowerCase()}{' '}
                  payment{recurringCount > 1 ? 's' : ''}
                </span>
                {' · '}${(chargeSubtotalCents / 100).toFixed(2)} per{' '}
                {getFrequencyInfo(recurringFrequency).unit}
              </p>
            </div>
          )}

          {/* Processing fee toggle */}
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={addProcessingFees}
              onChange={(e) => setAddProcessingFees(e.target.checked)}
              className="mt-1 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <span className="text-xs text-gray-600">{env.onlineFeeMessage}</span>
          </label>

          {addProcessingFees && processingFees > 0 && (
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">
                Processing fees
                {isSpreadPayments ? ` / ${getFrequencyInfo(recurringFrequency).unit}` : ''}
              </span>
              <span className="text-gray-700">${(processingFees / 100).toFixed(2)}</span>
            </div>
          )}

          <div className="flex justify-between text-lg font-bold">
            <span>
              {isSpreadPayments ? `Per ${getFrequencyInfo(recurringFrequency).unit}` : 'Total'}
            </span>
            <span className="text-primary-600">
              ${(finalTotal / 100).toFixed(2)}
              {isSpreadPayments && (
                <span className="text-sm font-normal text-primary-400">
                  {' '}
                  / {getFrequencyInfo(recurringFrequency).unit}
                </span>
              )}
            </span>
          </div>
        </div>

        {/* Auto-Repeat */}
        <div
          className={clsx(
            'rounded-2xl border-2 p-4 space-y-4 transition-all',
            isRecurring
              ? 'bg-primary-50 border-primary-300 shadow-md shadow-primary-100'
              : 'bg-white border-gray-200 shadow-sm'
          )}
        >
          {/* Toggle row */}
          <div className="flex items-center justify-between">
            <div>
              <p className="text-base font-bold text-gray-900">Repeat this Donation</p>
              <p className="text-xs text-gray-500 mt-0.5">
                {isRecurring ? 'Auto-charged on your chosen schedule' : 'One-time only'}
              </p>
            </div>
            {/* Toggle switch */}
            <button
              type="button"
              role="switch"
              aria-checked={isRecurring}
              onClick={() => {
                if (isRecurring) {
                  setRecurringFrequency(DEFAULT_RECURRING_FREQUENCY);
                  setRecurringCount(DEFAULT_RECURRING_COUNT);
                }
                setIsRecurring(!isRecurring);
              }}
              className={clsx(
                'relative inline-flex h-7 w-13 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2',
                isRecurring ? 'bg-primary-600' : 'bg-gray-300'
              )}
            >
              <span
                className={clsx(
                  'pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow-lg ring-0 transition duration-200 ease-in-out',
                  isRecurring ? 'translate-x-6' : 'translate-x-0'
                )}
              />
            </button>
          </div>

          {isRecurring && (
            <div className="space-y-4 pt-1">
              {/* Frequency selector */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wide">
                  How often?
                </label>
                <div className="flex gap-2">
                  {RECURRING_FREQUENCIES.map(({ label, value, defaultCount }) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => {
                        setRecurringFrequency(value);
                        setRecurringCount(defaultCount);
                      }}
                      className={clsx(
                        'flex-1 py-3 px-2 text-base font-bold rounded-xl border-2 transition-all',
                        recurringFrequency === value
                          ? 'bg-primary-600 text-white border-primary-600 shadow-md shadow-primary-200 scale-[1.03]'
                          : 'bg-white text-gray-700 border-gray-300 hover:border-primary-400 hover:bg-primary-50'
                      )}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Count selector */}
              <div>
                <label
                  htmlFor="da-recurringCount"
                  className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wide"
                >
                  {getFrequencyInfo(recurringFrequency).countQuestion}
                </label>
                <select
                  id="da-recurringCount"
                  value={recurringCount}
                  onChange={(e) => setRecurringCount(Number(e.target.value))}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl text-gray-900 text-base font-medium focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white"
                >
                  {(RECURRING_COUNTS[recurringFrequency] ?? []).map(({ value, label }) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>

        {/* Donor info summary */}
        <div className="bg-gray-50 rounded-2xl p-4 text-sm space-y-1 border border-gray-100">
          <div>
            <span className="text-gray-500">Name:</span>{' '}
            <span className="font-medium text-gray-900">
              {env.onlineEnableAnonymous && isDonateAnonymously ? 'Anonymous' : donorName}
            </span>
          </div>
          <div>
            <span className="text-gray-500">Email:</span>{' '}
            <span className="font-medium text-gray-900">{donorEmail}</span>
          </div>
        </div>

        {stripeError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-red-800 text-sm">{stripeError.message}</p>
          </div>
        )}

        <div className="flex gap-3">
          <button
            onClick={() => setStep('info')}
            disabled={stripeLoading}
            className="flex-1 py-3 bg-gray-100 text-gray-700 font-semibold rounded-xl hover:bg-gray-200 disabled:opacity-50 transition-colors flex items-center justify-center gap-1.5"
          >
            <ArrowLeftIcon className="h-4 w-4" />
            Back
          </button>
          <button
            onClick={handleConfirmPayment}
            disabled={stripeLoading}
            className="flex-1 py-4 bg-linear-to-r from-primary-600 to-primary-500 text-white font-semibold rounded-xl hover:from-primary-700 hover:to-primary-600 disabled:opacity-50 transition-all shadow-md hover:shadow-lg disabled:shadow-none flex items-center justify-center gap-2"
          >
            {stripeLoading ? (
              <>
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24" fill="none">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                  />
                </svg>
                Processing…
              </>
            ) : (
              <>
                <CheckCircleIcon className="h-5 w-5" />
                Pay ${(finalTotal / 100).toFixed(2)}
                {isSpreadPayments && (
                  <span className="text-sm opacity-75">
                    {' '}
                    / {getFrequencyInfo(recurringFrequency).unit}
                  </span>
                )}
              </>
            )}
          </button>
        </div>
      </div>
    );
  }

  return null;
}
